import { getFormProps, getInputProps, getTextareaProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Heading,
  HelperText,
  Label,
  Option,
  Select,
  Stack,
  TextArea,
  TextAreaControl,
  TextInput,
  TextInputControl,
  TextInputSlot,
} from "@deliveryhero/cape-core";
import { ArrowBackwardIcon, EditIcon, PhoneIcon, SearchIcon } from "@deliveryhero/cape-icons";
import { useEffect, useState } from "react";
import { Form, Link, redirect, useActionData, useNavigation } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { env } from "~/config";
import { resolveTheme } from "~/lib/brand";
import { mapMarketsToEntityIds } from "~/lib/global-entities";
import { i18n } from "~/lib/i18n";
import { getBrandName } from "~/lib/meta";
import { DH_MARKETS, REGISTER_FIXTURES } from "~/mocks/fixtures/register.fixtures";
import type { Route } from "./+types/route";
import * as styles from "./register.css";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BLOCKED_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com"];

const VENDOR_RANGES = ["1-10", "11-50", "51-200", "200+"] as const;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const RegisterSchema = z.object({
  markets: z.array(z.string().min(1)).min(1, "register.markets.error.required"),
  firstName: z
    .string({ required_error: "register.firstName.error.required" })
    .min(1, "register.firstName.error.required"),
  lastName: z
    .string({ required_error: "register.lastName.error.required" })
    .min(1, "register.lastName.error.required"),
  email: z
    .string({ required_error: "register.email.error.required" })
    .min(1, "register.email.error.required")
    .email("register.email.error.invalid")
    .refine((v) => !BLOCKED_DOMAINS.includes(v.split("@")[1]?.toLowerCase() ?? ""), {
      message: "register.email.error.nonWork",
    }),
  phone: z
    .string({ required_error: "register.phone.error.required" })
    .min(1, "register.phone.error.required"),
  company: z
    .string({ required_error: "register.company.error.required" })
    .min(1, "register.company.error.required"),
  website: z
    .string({ required_error: "register.website.error.required" })
    .min(1, "register.website.error.required")
    .transform((v) => (v.includes("://") ? v : `https://${v}`))
    .pipe(
      z
        .string()
        .url("register.website.error.invalid")
        .refine(
          (v) => { try { return new URL(v).hostname.includes("."); } catch { return false; } },
          "register.website.error.invalid",
        ),
    ),
  vendors: z.enum(VENDOR_RANGES, {
    errorMap: () => ({ message: "register.vendors.error.required" }),
  }),
  pgpKey: z
    .string({ required_error: "register.pgpKey.error.required" })
    .min(1, "register.pgpKey.error.required"),
  terms: z
    .string()
    .optional()
    .refine((v) => v === "on", {
      message: "register.terms.error.required",
    }),
});

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export function meta({ matches }: Route.MetaArgs) {
  const brandName = getBrandName(matches);
  return [
    { title: i18n.t("register.meta.title", { brandName }) },
    { name: "description", content: i18n.t("register.meta.description") },
  ];
}

// ---------------------------------------------------------------------------
// Action
// ---------------------------------------------------------------------------

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema: RegisterSchema });

  if (submission.status !== "success") {
    return { lastResult: submission.reply() };
  }

  const { email, company, phone, markets, firstName, lastName, website, vendors, pgpKey, terms } =
    submission.value;
  const encoded = encodeURIComponent(email);

  // Note: plain object returns (no data() wrapper) are intentional here.
  // Using data() with non-2xx status codes triggers React Router v7's Single
  // Fetch TurboStream path in a way that Miniflare cannot handle in dev mode,
  // causing a "fetch failed" TCP-level crash. HTTP semantics are preserved at
  // the BFF layer via the real API.

  // Fixture fallback when no API URL is configured (local dev / CI preview).
  if (!env.apiBaseUrl) {
    if (email === REGISTER_FIXTURES.emails.vboFailure) {
      return {
        lastResult: submission.reply({
          formErrors: ["register.error.submissionFailed"],
        }),
      };
    }
    return redirect(`/auth/register/confirm?email=${encoded}`);
  }

  const { brand } = resolveTheme(request);
  const globalEntityIds = mapMarketsToEntityIds(markets ?? [], brand);

  let resp: Response;
  try {
    resp = await fetch(`${env.apiBaseUrl}/api/v1/partners`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        company_name: company,
        phone: phone ?? "",
        markets: globalEntityIds,
        first_name: firstName,
        last_name: lastName,
        company_website: website,
        vendor_count: vendors,
        pgp_key: pgpKey,
        tc_accepted: terms === "on",
      }),
    });
  } catch {
    return {
      lastResult: submission.reply({
        formErrors: ["register.error.submissionFailed"],
      }),
    };
  }

  if (!resp.ok) {
    if (resp.status === 502) {
      return {
        lastResult: submission.reply({
          formErrors: ["register.error.submissionFailed"],
        }),
      };
    }
    return {
      lastResult: submission.reply({
        formErrors: ["error.default.message"],
      }),
    };
  }

  return redirect(`/auth/register/confirm?email=${encoded}`);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function RegisterPage() {
  const { t } = useTranslation();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const [marketsOpen, setMarketsOpen] = useState(false);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  // draft: in-dialog selection — only committed to selectedMarkets on Apply
  const [draft, setDraft] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  // isMobile: drives inline styles that override CAPE dialog dimensions
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  function openMarketsDialog() {
    setDraft(selectedMarkets);
    setSearch("");
    setMarketsOpen(true);
  }

  function toggleDraft(value: string) {
    setDraft((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }

  function handleMarketsApply() {
    setSelectedMarkets(draft);
    setMarketsOpen(false);
  }

  const filteredMarkets = DH_MARKETS.filter((m) =>
    m.label.toLowerCase().includes(search.toLowerCase()),
  );

  const [form, fields] = useForm({
    lastResult: actionData?.lastResult,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: RegisterSchema });
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  const emailErrors = fields.email.errors;
  const marketsErrors = fields.markets.errors;
  const firstNameErrors = fields.firstName.errors;
  const lastNameErrors = fields.lastName.errors;
  const phoneErrors = fields.phone.errors;
  const companyErrors = fields.company.errors;
  const websiteErrors = fields.website.errors;
  const vendorsErrors = fields.vendors.errors;
  const pgpKeyErrors = fields.pgpKey.errors;
  const termsErrors = fields.terms.errors;

  return (
    <main className={styles.container}>
      <Box className={styles.card}>
        {/* Back to login — arrow at top */}
        <Link to="/auth/login" className={styles.backLink} data-testid="back-link">
          <ArrowBackwardIcon aria-hidden="true" width="var(--cp-icon-sizing-small)" height="var(--cp-icon-sizing-small)" />
          {t("register.cta.backToLogin")}
        </Link>

        {/* Header */}
        <Box className={styles.header}>
          <Heading level={1} className={styles.title}>
            {t("register.heading")}
          </Heading>
          <Box as="p" className={styles.subtitle}>
            {t("register.subtitle")}
          </Box>
        </Box>

        {/* Form-level error banner */}
        {form.errors != null && form.errors.length > 0 && (
          <Alert variant="error" style={{ marginBlockEnd: "var(--cp-spacing-large)" }}>
            {form.errors.map((key) => t(key)).join(" ")}
          </Alert>
        )}

        {/* Registration form */}
        <Form method="post" {...getFormProps(form)}>
          <Stack direction="column" spacing="--cp-spacing-large">
            {/* Markets — hidden inputs carry selected values; Dialog handles selection UI */}
            <Box>
              <Label htmlFor="markets-trigger" required>
                {t("register.markets.label")}
              </Label>

              {/* Hidden inputs for Conform array field — one per selected market */}
              {selectedMarkets.map((m) => (
                <input type="hidden" key={m} name={fields.markets.name} value={m} />
              ))}

              {/* Read-only TextInput trigger — mirrors prototype pattern */}
              <TextInput
                onClick={openMarketsDialog}
                data-testid="markets-trigger"
                {...(marketsErrors?.length ? { validationState: "error" as const } : {})}
              >
                <TextInputControl
                  id="markets-trigger"
                  readOnly
                  value={
                    selectedMarkets.length > 0
                      ? t("register.markets.selected", { count: selectedMarkets.length })
                      : ""
                  }
                  placeholder={t("register.markets.placeholder")}
                  className={styles.marketsTriggerControl}
                  aria-invalid={!!marketsErrors?.length || undefined}
                  aria-describedby={marketsErrors?.length ? fields.markets.errorId : undefined}
                  disabled={isSubmitting}
                />
                <TextInputSlot>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="xsmall"
                    onClick={openMarketsDialog}
                    aria-label={t("register.markets.editLabel")}
                    disabled={isSubmitting}
                  >
                    {/* Explicit dimensions guard against the in-button
                        `svg { width: auto }` rule winning the cascade race
                        with the cape-icons size rule after HMR. */}
                    <EditIcon
                      style={{
                        width: "var(--cp-icon-sizing-medium, 24px)",
                        height: "var(--cp-icon-sizing-medium, 24px)",
                      }}
                    />
                  </Button>
                </TextInputSlot>
              </TextInput>

              {marketsErrors != null && marketsErrors.length > 0 && (
                <HelperText id={fields.markets.errorId} variant="error" role="alert">
                  {t(marketsErrors[0] ?? "")}
                </HelperText>
              )}

              {/* Markets Dialog — 475px desktop; full-screen on mobile via inline styles */}
              <Dialog
                open={marketsOpen}
                onOpenChange={setMarketsOpen}
                closeOnOutsideClick
                closeOnEsc
              >
                <DialogContent
                  style={
                    isMobile
                      ? {
                          width: "100%",
                          maxWidth: "none",
                          height: "100dvh",
                          maxHeight: "none",
                          borderRadius: 0,
                          margin: 0,
                          display: "flex",
                          flexDirection: "column",
                        }
                      : { width: "475px" }
                  }
                >
                  <DialogHeader>
                    <Heading level={2}>{t("register.markets.label")}</Heading>
                  </DialogHeader>
                  <DialogBody style={isMobile ? { flex: 1, minHeight: 0 } : undefined}>
                    <Box className={styles.dialogInner}>
                      {/* Search */}
                      <Box className={styles.searchField}>
                        <TextInput>
                          <TextInputSlot>
                            <Box
                              style={{
                                width: "var(--cp-icon-sizing-medium)",
                                height: "var(--cp-icon-sizing-medium)",
                                display: "flex",
                                color: "var(--cp-color-jacaranda-grey-50)",
                              }}
                            >
                              <SearchIcon />
                            </Box>
                          </TextInputSlot>
                          <TextInputControl
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("register.markets.search")}
                            autoComplete="off"
                          />
                        </TextInput>
                      </Box>

                      {/* Scrollable market list */}
                      <Box
                        className={styles.marketsList}
                        style={
                          isMobile
                            ? { flex: 1, height: "auto", minHeight: 0, overflowY: "auto" }
                            : undefined
                        }
                      >
                        <Stack direction="column" spacing="--cp-spacing-small">
                          {filteredMarkets.map((market) => (
                            <Box key={market.value} className={styles.marketItem}>
                              <Checkbox
                                id={`market-${market.value}`}
                                checked={draft.includes(market.value)}
                                onChange={() => toggleDraft(market.value)}
                              />
                              <Label
                                htmlFor={`market-${market.value}`}
                                style={{ cursor: "pointer", fontWeight: 400 }}
                              >
                                {market.label}
                              </Label>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Box>
                  </DialogBody>
                  <DialogFooter
                    style={
                      isMobile
                        ? {
                            position: "sticky",
                            bottom: 0,
                            backgroundColor: "var(--cp-color-white, #fff)",
                            boxShadow: "0 -2px 16px rgba(20,20,21,0.12)",
                          }
                        : undefined
                    }
                  >
                    <Button
                      variant="primary"
                      size="small"
                      type="button"
                      className={styles.fullWidth}
                      onClick={handleMarketsApply}
                    >
                      {t("register.markets.apply")}
                      {draft.length > 0 ? ` (${draft.length})` : ""}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Box>

            {/* First Name / Last Name — stacks on mobile via nameRow CSS */}
            <Box className={styles.nameRow}>
              <Box style={{ flex: 1 }}>
                <Label htmlFor={fields.firstName.id} required>
                  {t("register.firstName.label")}
                </Label>
                <TextInput
                  {...(firstNameErrors?.length ? { validationState: "error" as const } : {})}
                >
                  <TextInputControl
                    {...getInputProps(fields.firstName, { type: "text" })}
                    placeholder={t("register.firstName.placeholder")}
                    autoComplete="given-name"
                    disabled={isSubmitting}
                  />
                </TextInput>
                {firstNameErrors != null && firstNameErrors.length > 0 && (
                  <HelperText id={fields.firstName.errorId} variant="error" role="alert">
                    {t(firstNameErrors[0] ?? "")}
                  </HelperText>
                )}
              </Box>

              <Box style={{ flex: 1 }}>
                <Label htmlFor={fields.lastName.id} required>
                  {t("register.lastName.label")}
                </Label>
                <TextInput
                  {...(lastNameErrors?.length ? { validationState: "error" as const } : {})}
                >
                  <TextInputControl
                    {...getInputProps(fields.lastName, { type: "text" })}
                    placeholder={t("register.lastName.placeholder")}
                    autoComplete="family-name"
                    disabled={isSubmitting}
                  />
                </TextInput>
                {lastNameErrors != null && lastNameErrors.length > 0 && (
                  <HelperText id={fields.lastName.errorId} variant="error" role="alert">
                    {t(lastNameErrors[0] ?? "")}
                  </HelperText>
                )}
              </Box>
            </Box>

            {/* Work Email */}
            <Box>
              <Label htmlFor={fields.email.id} required>
                {t("register.email.label")}
              </Label>
              <TextInput {...(emailErrors?.length ? { validationState: "error" as const } : {})}>
                <TextInputControl
                  {...getInputProps(fields.email, { type: "email" })}
                  placeholder={t("register.email.placeholder")}
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </TextInput>
              {emailErrors != null && emailErrors.length > 0 && (
                <HelperText id={fields.email.errorId} variant="error" role="alert">
                  {t(emailErrors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            {/* Phone */}
            <Box>
              <Label htmlFor={fields.phone.id} required>
                {t("register.phone.label")}
              </Label>
              <TextInput {...(phoneErrors?.length ? { validationState: "error" as const } : {})}>
                <TextInputSlot>
                  <PhoneIcon aria-hidden="true" />
                </TextInputSlot>
                <TextInputControl
                  {...getInputProps(fields.phone, { type: "tel" })}
                  placeholder={t("register.phone.placeholder")}
                  autoComplete="tel"
                  disabled={isSubmitting}
                />
              </TextInput>
              {phoneErrors == null || phoneErrors.length === 0 ? (
                <HelperText>{t("register.phone.helper")}</HelperText>
              ) : (
                <HelperText id={fields.phone.errorId} variant="error" role="alert">
                  {t(phoneErrors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            {/* Company Name */}
            <Box>
              <Label htmlFor={fields.company.id} required>
                {t("register.company.label")}
              </Label>
              <TextInput {...(companyErrors?.length ? { validationState: "error" as const } : {})}>
                <TextInputControl
                  {...getInputProps(fields.company, { type: "text" })}
                  placeholder={t("register.company.placeholder")}
                  autoComplete="organization"
                  disabled={isSubmitting}
                />
              </TextInput>
              {companyErrors != null && companyErrors.length > 0 && (
                <HelperText id={fields.company.errorId} variant="error" role="alert">
                  {t(companyErrors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            {/* Company Website */}
            <Box>
              <Label htmlFor={fields.website.id} required>
                {t("register.website.label")}
              </Label>
              <TextInput {...(websiteErrors?.length ? { validationState: "error" as const } : {})}>
                <TextInputControl
                  {...getInputProps(fields.website, { type: "url" })}
                  placeholder={t("register.website.placeholder")}
                  autoComplete="url"
                  disabled={isSubmitting}
                />
              </TextInput>
              {websiteErrors != null && websiteErrors.length > 0 && (
                <HelperText id={fields.website.errorId} variant="error" role="alert">
                  {t(websiteErrors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            {/* Number of Vendors */}
            <Box>
              <Label htmlFor={fields.vendors.id} required>
                {t("register.vendors.label")}
              </Label>
              <Select
                id={fields.vendors.id}
                name={fields.vendors.name}
                defaultValue={fields.vendors.defaultValue ?? ""}
                error={!!vendorsErrors?.length}
                disabled={isSubmitting}
                aria-describedby={vendorsErrors?.length ? fields.vendors.errorId : undefined}
                style={{ width: "100%" }}
              >
                <Option value="" disabled>
                  {t("register.vendors.placeholder")}
                </Option>
                {VENDOR_RANGES.map((range) => (
                  <Option key={range} value={range}>
                    {range}
                  </Option>
                ))}
              </Select>
              {vendorsErrors != null && vendorsErrors.length > 0 && (
                <HelperText id={fields.vendors.errorId} variant="error" role="alert">
                  {t(vendorsErrors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            {/* PGP Public Key */}
            <Box>
              <Label htmlFor={fields.pgpKey.id} required>
                {t("register.pgpKey.label")}
              </Label>
              <TextArea {...(pgpKeyErrors?.length ? { validationState: "error" as const } : {})}>
                <TextAreaControl
                  {...getTextareaProps(fields.pgpKey)}
                  placeholder={t("register.pgpKey.placeholder")}
                  rows={5}
                  disabled={isSubmitting}
                  style={{ resize: "vertical" }}
                />
              </TextArea>
              <Box className={styles.helperText}>{t("register.pgpKey.helper")}</Box>
              {pgpKeyErrors != null && pgpKeyErrors.length > 0 && (
                <HelperText id={fields.pgpKey.errorId} variant="error" role="alert">
                  {t(pgpKeyErrors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            {/* Terms & Conditions */}
            <Box>
              <Box className={styles.termsRow}>
                <Checkbox
                  id={fields.terms.id}
                  name={fields.terms.name}
                  value="on"
                  disabled={isSubmitting}
                  aria-describedby={termsErrors?.length ? fields.terms.errorId : undefined}
                />
                <label htmlFor={fields.terms.id} className={styles.termsText}>
                  {t("register.terms.prefix")}
                  {/* BLK-01: Replace # hrefs with Legal-provided URLs before launch */}
                  <a href="#" className={styles.termsLink}>
                    {t("register.terms.termsLink")}
                  </a>
                  {t("register.terms.separator")}
                  <a href="#" className={styles.termsLink}>
                    {t("register.terms.privacyLink")}
                  </a>
                </label>
              </Box>
              {termsErrors != null && termsErrors.length > 0 && (
                <HelperText id={fields.terms.errorId} variant="error" role="alert">
                  {t(termsErrors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            {/* Primary CTA */}
            <Button
              type="submit"
              variant="primary"
              size="small"
              className={styles.fullWidth}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("register.submitting") : t("register.submit")}
            </Button>
          </Stack>
        </Form>
      </Box>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Error boundary — required on every route file (developer conventions)
// ---------------------------------------------------------------------------

export function ErrorBoundary() {
  const { t } = useTranslation();
  return (
    <main className={styles.container}>
      <Box className={styles.card}>
        <Alert variant="error">{t("error.default.message")}</Alert>
      </Box>
    </main>
  );
}
